import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { useIsMounted } from '../../components/React';
import {
  arraysIdentical,
  config,
  customerLicenseTypes,
  displayJson,
  logError,
  objectsIdentical,
  registerLoader,
  completeLoader,
  suspendLoader,
  resumeLoader,
  logDev,
} from '../../utils';
import useAuth from '../auth/useAuth';
import usePushEvent from '../push/usePushEvent';

import TownConfigContext from './TownConfigContext';

// Register granular loaders at module load time (before React renders)
const loaderIds = {
  baseConfig: registerLoader('TownConfigProvider.baseConfig'),
  zones: registerLoader('TownConfigProvider.zones'),
};

const initialState = {
  loadedFor: undefined,
  baseConfigPending: false,
  baseConfig: undefined,
  zones: [],
  configReady: false,
  zonesPending: false,
  zonesReady: false,
  ready: false,
  groupsPending: false,
  groupsReady: false,
  staff: undefined,
  staffPending: false,
  staffReady: false,
  groupsMap: new Map(),
  imagesMap: new Map(),
  postGroupCallback: undefined,
  ccatToGcat: undefined,
  invites: {
    incoming: undefined,
    outgoing: undefined,
  },
  invitesPending: {
    incoming: false,
    outgoing: false,
  },
  invitesError: {
    incoming: false,
    outgoing: false,
  },
  invitesReady: {
    incoming: false,
    outgoing: false,
  },
  licenses: undefined,
  licensesPending: false,
  licensesReady: false,
  licensesError: false,
  sites: undefined,
  sitesPending: false,
  sitesReady: false,
  sitesError: false,
  privilegesMap: new Map(),
  privilegesPending: new Set(),
  pendingDeletionsByRlid: new Map(),
  pendingDeletionsPending: false,
  pendingDeletionsReady: false,
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return ({
        ...initialState,
        loadedFor: action.loadedFor,
      });

    case 'fetchBaseConfig':
      if (state.baseConfigPending) return state;
      return ({
        ...state,
        baseConfigPending: true,
      });

    case 'setBaseConfig':
      // console.log(`!!! town baseConfig = ${displayJson(action.data)}`);

      return ({
        ...state,
        baseConfig: action.data,
        baseConfigPending: false,
        baseConfigReady: true,
        ready: !!state.zonesReady,
      });

    case 'fetchZones':
      if (state.zonesPending) return state;
      return ({
        ...state,
        zonesPending: true,
      });

    case 'setZones':
      if (!action.data.length) return state;
      return ({
        ...state,
        zones: action.data,
        zonesPending: false,
        zonesReady: true,
        ready: !!state.baseConfigReady,
      });

    case 'fetchGroups':
      if (state.groupsPending) return state;
      return ({
        ...state,
        groupsPending: true,
      });

    case 'setGroups': {
      // piid is undefined for home server groups
      const {
        piid,
        cuid,
        lbl: prtnrLbl,
      } = action;
      // console.log(`TownConfigfProvider action=${displayJson(action)}`);
      let anyGroupChanged;

      // Build a map keeping only groups from other piids
      // Groups from same piid will be replaced
      const newGroupsMap = new Map();
      state.groupsMap.forEach((existingGroup, gpid) => {
        // Keep groups from different piids
        // For home server (piid undefined), keep groups that have a piid
        // For partner (piid defined), keep groups that have different piid or no piid
        if (piid) {
          // Partner update: keep groups without piid or with different piid
          if ((!existingGroup.piid) || (existingGroup.piid !== piid)) {
            newGroupsMap.set(gpid, existingGroup);
          }
        } else
          // Home update: keep groups that have a piid (partner groups)
          if (existingGroup.piid) {
            newGroupsMap.set(gpid, existingGroup);
          }
      });

      // // eslint-disable-next-line no-debugger
      // debugger;

      const newGroups = action.data.map((group) => {
        const { grpAttr, grpExternal, ...otherInfo } = group;
        const {
          lbl, desc, logoId, isExternal, partners,
          grpPrntGpid, grpPrntRlid, privileges,
        } = grpAttr;
        let thisGroupChanged = false;
        // console.log(`TownConfigfProvider ${lbl} group.gcat=${displayJson(group.gcat)}`);
        const primaryGcat = group.gcat.find((gcat) => (gcat.primary));
        const primaryGcatCode = primaryGcat?.code;
        const existingGroup = state.groupsMap.get(group.grpGpid);

        if (
          ((!existingGroup)
            || (existingGroup.prtnrLbl !== prtnrLbl)
            || (existingGroup.piid !== piid)
            || (!objectsIdentical(existingGroup.grpSchdl, group.grpSchdl, 2))
            || (!objectsIdentical(existingGroup.isExternal, isExternal, 2))
            || (!objectsIdentical(existingGroup.partners, partners, 2))
            || (existingGroup.grpLbl !== lbl)
            || (existingGroup.grpDesc !== desc)
            || (existingGroup.logoId !== logoId)
            || (!arraysIdentical(existingGroup.grpCcatDomnCodes, group.grpCcatDomnCodes, false))
            || (existingGroup.grpPrntGpid !== grpPrntGpid)
            || (existingGroup.grpPrntRlid !== grpPrntRlid)
            || (existingGroup.grpParentCasesVisibility !== group.grpParentCasesVisibility)
            || (!arraysIdentical(existingGroup.trgtFldrIds, group.trgtFldrIds, false))
          )
        ) {
          anyGroupChanged = true;
          thisGroupChanged = true;
        }

        if (!thisGroupChanged) return existingGroup;

        const updatedGroup = {
          ...otherInfo,
          grpLbl: lbl,
          grpDesc: desc,
          logoId,
          primaryGcatCode,
          cuid,
          prtnrLbl,
          grpPrivileges: privileges || [],
          trgtFldrIds: group.trgtFldrIds || [],
        };

        // Attach piid to identify which server this group comes from
        if (piid) {
          updatedGroup.piid = piid;
        }

        if (grpExternal) {
          updatedGroup.isExternal = isExternal || {};
        }

        if (partners) updatedGroup.partners = partners;

        return (updatedGroup);
      });

      // Check if we need to update - count changed if new groups or different count for this piid
      const oldPiidCount = Array.from(state.groupsMap.values())
        .filter((g) => ((piid && (g.piid === piid)) || ((!piid) && (!g.piid)))).length;
      if (newGroups.length !== oldPiidCount) anyGroupChanged = true;

      if (!anyGroupChanged) return state;

      newGroups.forEach((group) => {
        newGroupsMap.set(
          group.grpGpid,
          group,
        );
      });

      // logDev(`town newGroupsMap=${displayJson(newGroupsMap)}`);

      return ({
        ...state,
        groupsReady: true,
        groupsPending: false,
        postGroupCallback: action.callback,
        groupsMap: newGroupsMap,
      });
    }

    case 'setGroupsCallbackDone':
      return ({
        ...state,
        postGroupCallback: undefined,
      });

    case 'fetchStaff':
      if (state.staffPending) return state;
      return ({
        ...state,
        staffPending: true,
      });

    case 'staffFailed':
      return ({
        ...state,
        staffPending: false,
      });

    case 'setStaff': {
      const newStaffMap = new Map();

      action.staff.forEach((member) => {
        newStaffMap.set(
          member.rlid,
          member,
        );
      });

      return ({
        ...state,
        staffPending: false,
        staff: action.staff,
        staffMap: newStaffMap,
      });
    }

    case 'getLogo': {
      const newImagesMap = new Map(state.imagesMap);

      // console.log(`withTownConfig getLogo logoId=${action.logoId}`);
      newImagesMap.set(
        action.logoId,
        null,
      );
      return ({
        ...state,
        imagesMap: newImagesMap,
      });
    }

    case 'setLogo': {
      const newImagesMap = new Map(state.imagesMap);

      if (!action.logo) {
        // console.log(`withTownConfig set empty logo logoId=${action.logoId}???`);
        return state;
      }

      // console.log(`withTownConfig set logo for logoId=${action.logoId}`);
      newImagesMap.set(
        action.logoId,
        action.logo,
      );
      return ({
        ...state,
        imagesMap: newImagesMap,
      });
    }

    case 'setCcatToGcat':
      return ({
        ...state,
        ccatToGcat: action.data,
      });

    case 'fetchInvites': {
      const { incoming } = action;
      const key = incoming ? 'incoming' : 'outgoing';

      if (state.invitesPending[key]) return state;

      return ({
        ...state,
        invitesPending: {
          ...state.invitesPending,
          [key]: true,
        },
      });
    }

    case 'setInvites': {
      const {
        invites,
        incoming,
        error,
      } = action;
      const key = incoming ? 'incoming' : 'outgoing';

      const newState = { ...state };

      newState.invitesPending = {
        ...newState.invitesPending,
        [key]: false,
      };

      if (error) {
        newState.invitesError = {
          ...newState.invitesError,
          [key]: true,
        };
      } else {
        newState.invitesError = {
          ...newState.invitesError,
          [key]: false,
        };
        newState.invitesReady = {
          ...newState.invitesReady,
          [key]: true,
        };

        newState.invites = {
          ...newState.invites,
          [key]: invites,
        };
      }

      return newState;
    }

    case 'fetchLicenses':
      if (state.licensesPending) return state;
      return ({
        ...state,
        licensesPending: true,
      });

    case 'setLicenses': {
      return ({
        ...state,
        licensesPending: false,
        licenses: action.licenses,
        licensesError: action.error,
        licensesReady: !action.error,
      });
    }

    case 'fetchSites':
      if (state.sitesPending) return state;
      return ({
        ...state,
        sitesPending: true,
      });

    case 'setSites': {
      return ({
        ...state,
        sitesPending: false,
        sites: action.sites,
        sitesError: action.error,
        sitesReady: !action.error,
      });
    }

    case 'fetchPrivileges': {
      const key = action.piid || 'home';
      if (state.privilegesPending.has(key)) return state;
      const newPending = new Set(state.privilegesPending);
      newPending.add(key);
      return ({
        ...state,
        privilegesPending: newPending,
      });
    }

    case 'setPrivileges': {
      const key = action.piid || 'home';
      const newPending = new Set(state.privilegesPending);
      newPending.delete(key);
      const newMap = new Map(state.privilegesMap);
      newMap.set(key, action.data);
      return ({
        ...state,
        privilegesPending: newPending,
        privilegesMap: newMap,
      });
    }

    case 'setPendingDeletions': {
      const newMap = new Map();
      (action.data || []).forEach((row) => {
        if (row.rlid) newMap.set(row.rlid, row);
      });
      return ({
        ...state,
        pendingDeletionsByRlid: newMap,
        pendingDeletionsPending: false,
        pendingDeletionsReady: true,
      });
    }

    case 'setPendingDeletionsPending':
      return ({
        ...state,
        pendingDeletionsPending: !!action.pending,
      });

    default:
      throw new Error();
  }
};

function TownConfigProvider(props) {
  const {
    children,
  } = props;
  const { auth } = useAuth();
  const {
    info: userInfo,
    authStatus,
    superagentGet,
    superagentGetAsync,
    superagentPostAsync,
    cstmrGet,
  } = auth;
  const {
    cuid,
    rlid,
    partners,
    privlg,
  } = userInfo || {};

  // Standing gate of /users/role/deletion/fromUser/list and of the received side
  // of /partners/list. Skip those fetches for everyone else so login is silent.
  const isCustomerManager = (!!privlg?.includes('admin'))
                          || (!!privlg?.includes('manager'))
                          || (!!privlg?.includes('super'));
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const isMountedRef = useIsMounted();

  // Everything here belongs to one session. A tab can be handed another one without
  // ever passing through the logged-out state — an admin login in a tab whose token
  // another tab already replaced — and would otherwise pair this customer with the
  // previous one's logos and groups. The reset lands only after a render, so until
  // then consumers are served the empty state rather than the previous session's.
  const sessionKey = userInfo ? `${cuid}/${rlid}` : undefined;
  const sessionState = (state.loadedFor === sessionKey) ? state : initialState;

  useEffect(
    () => {
      if (state.loadedFor !== sessionKey) {
        dispatch({
          type: 'reset',
          loadedFor: sessionKey,
        });
      }
    },
    [state.loadedFor, sessionKey],
  );

  // Suspend/resume all loaders based on auth status
  // Config cannot be loaded when logged out
  useEffect(
    () => {
      if (authStatus === 'loggedOut') {
        suspendLoader(loaderIds.baseConfig);
        suspendLoader(loaderIds.zones);
      } else if (authStatus === 'loggedIn') {
        resumeLoader(loaderIds.baseConfig);
        resumeLoader(loaderIds.zones);
      }
      // authStatus === 'init': loaders stay pending (waiting)
    },
    [authStatus],
  );

  // Complete individual loaders as each resource becomes available
  // Note: authStatus dependency ensures re-evaluation after login (data may already be cached)
  useEffect(
    () => {
      if ((authStatus === 'loggedIn') && state.baseConfigReady) {
        completeLoader(loaderIds.baseConfig);
      }
    },
    [state.baseConfigReady, authStatus],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn') && state.zonesReady) {
        completeLoader(loaderIds.zones);
      }
    },
    [state.zonesReady, authStatus],
  );

  const refreshCustomerConfig = useCallback(
    (init) => {
      if ((authStatus === 'loggedIn') && userInfo) {
        if ((!state.baseConfigPending)
            && ((!init) || (!state.baseConfigReady))
        ) {
          dispatch({ type: 'fetchBaseConfig' });

          const url = config.town.back.urlPrefix
                    + config.town.back.info.getConfig;

          // console.log(`withTownConfig get config from ${url}`);
          superagentGet(
            url,
            ['Content-Type', 'application/json'], // headers
            (err, res) => {
              if (!err) {
                const newConfig = res.body.rslt.data[0];
                dispatch({
                  type: 'setBaseConfig',
                  data: newConfig,
                });
              }
            },
          );
        }
      }
    },
    [authStatus, superagentGet, userInfo, state.baseConfigPending, state.baseConfigReady],
  );

  useEffect(
    () => { refreshCustomerConfig(true); },
    [refreshCustomerConfig],
  );

  // Get city outlines geozones
  //
  useEffect(
    () => {
      if ((authStatus === 'loggedIn') && userInfo) {
        if ((!state.zones.length) && (!state.zonesPending)) {
          dispatch({ type: 'fetchZones' });
          const url = config.town.back.urlPrefix
                  + config.town.back.info.geoZones.getTownPoly;

          // console.log(`withTownConfig get zones from ${url}`);
          superagentGet(
            url,
            ['Content-Type', 'application/json'], // headers
            (err, res) => {
              if (!err) {
                dispatch({
                  type: 'setZones',
                  data: res.body.rslt.data,
                });
              }
            },
          );
        }
      }
    },
    [authStatus, superagentGet, userInfo, state.zones.length, state.zonesPending],
  );

  // Get groups present on whole town
  //
  const getGroups = useCallback(
    async (callback) => {
      if (authStatus === 'loggedIn') {
        dispatch({ type: 'fetchGroups' });
        let error;

        // Fetch home server groups (piid: undefined)
        try {
          const res = await cstmrGet({
            urlPath: config.town.back.info.getGroups,
          });

          if (!(isMountedRef.current)) {
            return;
          }

          dispatch({
            type: 'setGroups',
            // piid: undefined,
            data: res.body.rslt.data,
            callback,
          });
        } catch (err) {
          error = err;
          logError(
            `getGroups home server error=${error}`,
            'TownConfigProvider.getGroups.homeError',
            {
              err: error?.message ?? String(error),
              ...(error?.status && { status: error.status }),
            },
          );
        }

        if (!(isMountedRef.current)) {
          return;
        }

        // Fetch partner groups in parallel
        if (partners?.length) {
          const partnerResults = await Promise.all(
            partners.map(async (partner) => {
              const { piid, cuid, lbl } = partner;
              try {
                const partnerRes = await cstmrGet({
                  piid,
                  rlid,
                  urlPath: config.town.back.info.getGroups,
                });

                if (partnerRes.status !== 200) {
                  logError(
                    `getGroups partner ${piid} status=${partnerRes.status}`,
                    'TownConfigProvider.getGroups.partnerStatus',
                    {
                      piid,
                      status: partnerRes?.status,
                    },
                  );
                  return null;
                }

                return {
                  piid,
                  groups: partnerRes.body.rslt.data,
                  cuid,
                  lbl,
                };
              } catch (err) {
                logError(
                  `getGroups partner ${piid} err=${err}`,
                  'TownConfigProvider.getGroups.partnerError',
                  {
                    piid,
                    err: err?.message,
                    ...(err?.status && { status: err.status }),
                  },
                );
                return null;
              }
            }),
          );

          if (!(isMountedRef.current)) {
            return;
          }

          partnerResults
            .filter((result) => (result !== null))
            .forEach((result) => {
              dispatch({
                type: 'setGroups',
                cuid: result.cuid,
                piid: result.piid,
                lbl: result.lbl,
                data: result.groups,
              });
            });
        }
      }
    },
    [authStatus, cstmrGet, partners, rlid, isMountedRef],
  );

  // Update settings for this customer
  //
  const recordCustomerSettings = useCallback(
    async (newSettings) => {
      // console.log(newSettings);
      dispatch({ type: 'fetchGroups' });
      const url = config.town.back.urlPrefix
                + config.town.back.info.settings.cstmr.set;

      // console.log(`withTownConfig get global groups from ${url}`);
      try {
        await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          newSettings,
        );
      } catch (err) {
        console.log(err);
        logError(
          `recordCustomerSettings failed, err=${displayJson(err)}`,
          'TownConfigProvider.recordCustomerSettings.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
            settingsKeys: newSettings && Object.keys(newSettings),
          },
        );
      }
      refreshCustomerConfig();
    },
    [superagentPostAsync, refreshCustomerConfig],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn') && userInfo) {
        if ((!state.groupsReady) && (!state.groupsPending)) {
          // Use void to make it clear we do not want to wait this async function:
          void getGroups();
        }
      }
    },
    [authStatus, userInfo, state.groupsReady, state.groupsPending, getGroups],
  );

  useEffect(
    () => {
      if (state.postGroupCallback) {
        state.postGroupCallback();
        dispatch({ type: 'setGroupsCallbackDone' });
      }
    },
    [state],
  );

  const refreshGroups = useCallback(
    (callback) => getGroups(callback),
    [getGroups],
  );

  // Get corresponding icons
  const getGroupLogo = useCallback(
    async (logoId, fake) => {
      if (authStatus === 'loggedIn') {
        dispatch({
          type: 'getLogo',
          logoId,
        });

        const url = config.town.back.urlPrefix
                  + config.town.back.info.getGroupLogo;

        try {
          const res = await superagentPostAsync(
            url,
            [],
            { logoId, fake },
            true,
          );

          if (isMountedRef.current) {
            if (res.status !== 200) {
              const error = new Error(`Response status=${res.status}`);
              logError(
                `getGroupLogo Error:${error}`,
                'TownConfigProvider.getGroupLogo.error',
                {
                  logoId,
                  status: res?.status,
                },
              );
              return;
            }

            const logo = URL.createObjectURL(res.body);
            // const media = res.body;
            dispatch({
              type: 'setLogo',
              logoId,
              logo,
            });
            // } else {
            //   console.log(`getGroupLogo ignored return data for logoId=${logoId}`);
          }
        } catch (err) {
          if (err.status === 404) {
            // console.log(`getGroupLogo not found for logoId=${logoId}`);
            return;
          }
          if (isMountedRef.current) {
            const error = err;
            console.log(`getMedia Error:${error}`);
          }
        }
      }
    },
    [isMountedRef, authStatus, superagentPostAsync],
  );

  // Get teams assignments
  const refreshCcatToGcat = useCallback(
    async () => {
      if (authStatus === 'loggedIn') {
        let res;
        let error;

        try {
          const url = config.town.back.urlPrefix
                    + config.town.back.groups.ccat.list;

          // console.log(`refreshAllStaff at ${url}`);
          res = await superagentGetAsync(
            url,
            ['Content-Type', 'application/json'],
          );
          if (res.status !== 200) {
            error = new Error(`Response status=${res.status}`);
          }

          dispatch({
            type: 'setCcatToGcat',
            data: res.body.rslt.data,
          });
        } catch (err) {
          error = err;
        }

        if (error) {
          logError(
            `refreshCcatToGcat error=${error?.message ?? error}`, // NOSONAR error stringified for logging
            'TownConfigProvider.refreshCcatToGcat.error',
            {
              err: error?.message ?? String(error), // NOSONAR error stringified for logging
              ...(error?.status && { status: error.status }),
              ...(res?.status && { resStatus: res.status }),
            },
          );
        }
      }
    },
    [superagentGetAsync, authStatus],
  );

  useEffect(
    () => {
      const gpidArray = Array.from(state.groupsMap.keys());
      // console.log(`gpidArray=${gpidArray}?`);
      const missingLogoGroupGpid = gpidArray
        .find((gpid) => {
          const thisGroup = state.groupsMap.get(gpid);
          return (thisGroup?.logoId && (!state.imagesMap.has(thisGroup.logoId)));
        });

      if (missingLogoGroupGpid) {
        const missingLogoGroup = state.groupsMap.get(missingLogoGroupGpid);
        // console.log(`missingLogoGroupGpid=${
        //   missingLogoGroupGpid}, group=${displayJson(missingLogoGroup)}`);
        // dispatch({ type: 'getGroupLogo', grpGpid: missingLogoGroupGpid.grpGpid });
        // Use void to make it clear we do not want to wait this async function:
        void getGroupLogo(missingLogoGroup.logoId, missingLogoGroup.grpFake);
      }
    },
    [state.groupsMap, getGroupLogo, state.imagesMap],
  );

  const refreshAllStaff = useCallback(
    async () => {
      dispatch({ type: 'fetchStaff' });

      let error;
      let res;
      try {
        const url = config.town.back.urlPrefix
                  + config.town.back.users.listStaff;

        // console.log(`refreshAllStaff at ${url}`);
        res = await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          {
            // sortBy: [{ key: 'id', order: 'desc' }],
            page: 0,
            size: 100,
          },
        );
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (error) {
        logError(
          `refreshAllStaff error=${error}`,
          'TownConfigProvider.refreshAllStaff',
          {
            err: error?.message ?? String(error),
            ...(error?.status && { status: error.status }),
          },
        );
        dispatch({ type: 'staffFailed' });
        return;
      }

      dispatch({
        type: 'setStaff',
        staff: res.body.rslt.data,
      });
    },
    [superagentPostAsync],
  );

  const refreshInvites = useCallback(
    async (incoming) => {
      let error;
      let res;
      dispatch({
        type: 'fetchInvites',
        incoming,
      });

      try {
        // console.log('refreshInvites');
        const url = config.town.back.urlPrefix
                  + config.town.back.partners.list;

        // console.log(`url=${url}`);

        res = await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          { incoming },
        );
        if (res.status !== 200) {
          throw new Error(`getSinglePollInfo status=${res.status}`);
        }

        // console.log(`invites: ${displayJson(res.body.rslt.data)}`);
      } catch (err) {
        logError(
          `refreshInvites err=${err}`,
          'TownConfigProvider.refreshInvites.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
            incoming,
          },
        );
        error = err;
      }

      const data = res?.body?.rslt?.data;

      dispatch({
        type: 'setInvites',
        // A privilege refusal answers 200 with a status flag instead of a list
        invites: (data?.[0]?.notAllowed) ? [] : data,
        incoming,
        error,
      });
    },
    [superagentPostAsync],
  );

  // The sent side of /partners/list is partnerView-gated server-side, and the
  // privilege is license-backed: a partner customer never holds it
  const homePrivileges = state.privilegesMap.get('home')?.privileges;
  const canSeeOutgoingInvites = !!homePrivileges?.some(
    (priv) => (['partnerView', 'partnerEdit'].includes(priv)),
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && isCustomerManager
          && (!state.invitesReady.incoming)
          && (!state.invitesError.incoming)
          && (!state.invitesPending.incoming)
      ) {
        // console.log('refreshInvites - incoming');
        // Use void to make it clear we do not want to wait this async function:
        void refreshInvites(true);
      }
    },
    [
      authStatus,
      userInfo,
      isCustomerManager,
      state.invitesError.incoming,
      state.invitesPending.incoming,
      state.invitesReady.incoming,
      refreshInvites,
    ],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && canSeeOutgoingInvites
          && (!state.invitesReady.outgoing)
          && (!state.invitesError.outgoing)
          && (!state.invitesPending.outgoing)
      ) {
        // console.log('refreshInvites - outgoing');
        // Use void to make it clear we do not want to wait this async function:
        void refreshInvites(false);
      }
    },
    [
      authStatus,
      userInfo,
      canSeeOutgoingInvites,
      state.invitesError.outgoing,
      state.invitesPending.outgoing,
      state.invitesReady.outgoing,
      refreshInvites,
    ],
  );

  const refreshLicenses = useCallback(
    async () => {
      let error;
      let res;
      dispatch({ type: 'fetchLicenses' });

      try {
        // console.log('refreshLicenses');
        const url = config.town.back.urlPrefix
                  + config.town.back.licenses.list;

        // console.log(`refreshLicenses url=${url}`);

        res = await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          { },
        );
        if (res.status !== 200) {
          throw new Error(`getSinglePollInfo status=${res.status}`);
        }

        // if (res.body.rslt.data.length) {
        //   console.log(`Last invite: ${displayJson(
        // res.body.rslt.data[res.body.rslt.data.length - 1])}`);
        // }
      } catch (err) {
        logError(
          `refreshLicenses err=${err}`,
          'TownConfigProvider.refreshLicenses.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
        error = err;
      }

      dispatch({
        type: 'setLicenses',
        licenses: res?.body?.rslt?.data,
        error,
      });
    },
    [superagentPostAsync],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && (!state.licensesPending)
          && (!state.licensesReady)
          && (!state.licensesError)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshLicenses();
      }
    },
    [
      authStatus,
      state.licensesError, state.licensesPending, state.licensesReady,
      refreshLicenses, userInfo,
    ],
  );

  const refreshSites = useCallback(
    async () => {
      let error;
      let res;
      dispatch({ type: 'fetchSites' });

      try {
        const url = config.town.back.urlPrefix
                  + config.town.back.sites.list;

        res = await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          {},
        );
        if (res.status !== 200) {
          throw new Error(`refreshSites status=${res.status}`);
        }
      } catch (err) {
        logError(
          `refreshSites err=${err}`,
          'TownConfigProvider.refreshSites.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
        error = err;
      }

      dispatch({
        type: 'setSites',
        sites: res?.body?.rslt?.data,
        error,
      });
    },
    [superagentPostAsync],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && (!state.sitesPending)
          && (!state.sitesReady)
          && (!state.sitesError)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshSites();
      }
    },
    [
      authStatus,
      state.sitesError, state.sitesPending, state.sitesReady,
      refreshSites, userInfo,
    ],
  );

  // Fetch privileges for home and partner contexts
  //
  const refreshPrivileges = useCallback(
    async (piid) => {
      const key = piid || 'home';
      dispatch({
        type: 'fetchPrivileges',
        piid,
      });

      try {
        let res;
        if (piid) {
          res = await cstmrGet({
            piid,
            rlid,
            urlPath: config.town.back.profile.privileges,
            log: true,
          });
        } else {
          const url = config.town.back.urlPrefix
                    + config.town.back.profile.privileges;

          res = await superagentGetAsync(
            url,
            ['Content-Type', 'application/json'],
          );
        }

        if (isMountedRef.current) {
          dispatch({
            type: 'setPrivileges',
            piid,
            data: res.body.rslt.data[0],
          });
        }
      } catch (err) {
        logError(
          `refreshPrivileges key=${key} err=${err}`,
          'TownConfigProvider.refreshPrivileges.error',
          {
            key,
            piid,
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
      }
    },
    [cstmrGet, rlid, superagentGetAsync, isMountedRef],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && (!state.privilegesPending.has('home'))
          && (!state.privilegesMap.has('home'))) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshPrivileges();
      }
    },
    [authStatus, userInfo, state.privilegesPending, state.privilegesMap, refreshPrivileges],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && userInfo
          && partners?.length) {
        partners.forEach((partner) => {
          const { piid } = partner;
          if ((!state.privilegesPending.has(piid))
              && (!state.privilegesMap.has(piid))) {
            // Use void to make it clear we do not want to wait this async function:
            void refreshPrivileges(piid);
          }
        });
      }
    },
    [authStatus, userInfo, partners, state.privilegesPending,
      state.privilegesMap, refreshPrivileges],
  );

  const getPrivileges = useCallback(
    (piid) => state.privilegesMap.get(piid || 'home'),
    [state.privilegesMap],
  );

  //
  // Subscribe to staffEdit push events to refresh groups and staff
  //
  usePushEvent(
    'staffEdit',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void refreshGroups();
      // Use void to make it clear we do not want to wait this async function:
      void refreshAllStaff();
    },
  );

  //
  // Keep the partnership lists live: the invited party viewing (partnerView)
  // or assigning staff (partnerAck) is otherwise invisible to the inviter until
  // a manual refresh. partnerRcv refreshes the recipient's incoming list;
  // partnerUpdate covers edits / status changes (and may alter group config).
  //
  usePushEvent(
    'partnerView',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void refreshInvites(false);
      // Use void to make it clear we do not want to wait this async function:
      void refreshInvites(true);
    },
  );
  usePushEvent(
    'partnerAck',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void refreshInvites(false);
      void refreshInvites(true);
    },
  );
  usePushEvent(
    'partnerRcv',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void refreshInvites(true);
    },
  );
  usePushEvent(
    'partnerUpdate',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void refreshInvites(false);
      void refreshInvites(true);
      // Use void to make it clear we do not want to wait this async function:
      void refreshGroups();
    },
  );

  //
  // Pending role-deletion requests at this customer (admin queue).
  // Fetched once on login and refreshed whenever a new request lands or
  // a pending one is cancelled — drives the "deletion requested" badge
  // on the Users members + Teams staff views.
  //
  const refreshPendingDeletions = useCallback(
    async () => {
      if (!isMountedRef.current) return;
      dispatch({
        type: 'setPendingDeletionsPending',
        pending: true,
      });
      try {
        const url = config.town.back.urlPrefix
                  + config.town.back.users.role.deletion.fromUser.list;
        const res = await superagentPostAsync(
          url,
          {},
          ['Content-Type', 'application/json'],
        );
        if (isMountedRef.current) {
          dispatch({
            type: 'setPendingDeletions',
            data: res.body?.rslt?.data ?? [],
          });
        }
      } catch (err) {
        logError(
          `refreshPendingDeletions err=${err}`,
          'TownConfigProvider.refreshPendingDeletions.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
        // Settle to a terminal (ready) state instead of just clearing the
        // pending flag — otherwise the trigger effect's `!pending && !ready`
        // guard refires immediately and hammers the endpoint in a loop.
        // Push events still refresh the queue afterwards.
        if (isMountedRef.current) {
          dispatch({
            type: 'setPendingDeletions',
            data: [],
          });
        }
      }
    },
    [superagentPostAsync, isMountedRef],
  );

  useEffect(
    () => {
      if ((authStatus === 'loggedIn')
          && isCustomerManager
          && (!state.pendingDeletionsPending)
          && (!state.pendingDeletionsReady)) {
        // Use void to make it clear we do not want to wait this async function:
        void refreshPendingDeletions();
      }
    },
    [
      authStatus,
      isCustomerManager,
      state.pendingDeletionsPending,
      state.pendingDeletionsReady,
      refreshPendingDeletions,
    ],
  );

  // Push events from notif-anon: a new request landed, or a pending one
  // was cancelled — either way the queue changed and should re-fetch.
  // notif-anon only fans these out to admin/manager rlids, but gate
  // anyway so the fetch is never tried by non-privileged users.
  usePushEvent(
    'roleDeletionPending',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (isCustomerManager) void refreshPendingDeletions();
    },
  );
  usePushEvent(
    'roleDeletionCancelled',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (isCustomerManager) void refreshPendingDeletions();
    },
  );
  // 'roleDeleted' fires when a request lands in 'completed' state
  // (user-initiated approved, or admin-initiated). 'roleDeletionRejected'
  // fires when the admin rejects a user-initiated request — the role
  // survives but its queue row moves out of 'pending'.
  // Both update the pending-deletions queue, so refresh.
  usePushEvent(
    'roleDeleted',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (isCustomerManager) void refreshPendingDeletions();
    },
  );
  usePushEvent(
    'roleDeletionRejected',
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (isCustomerManager) void refreshPendingDeletions();
    },
  );

  useEffect(
    () => { logDev(`privilegesMap=${displayJson(state.privilegesMap)}`); },
    [state.privilegesMap],
  );

  // A case tile only names its origin when there is more than one to tell apart:
  // each partnership is a source, and the home customer is one only if it licenses
  // reports of its own. Derived once here, never per tile.
  const multiCaseSources = useMemo(
    () => {
      const heldTypes = customerLicenseTypes(sessionState.licenses);
      const homeIsSource = heldTypes.has('qrReport') || heldTypes.has('appReport');
      return (((partners?.length ?? 0) + (homeIsSource ? 1 : 0)) >= 2);
    },
    [sessionState.licenses, partners],
  );

  const contextValue = useMemo(
    () => ({
      townConfig: {
        ...sessionState.baseConfig,
        zones: sessionState.zones,
        ready: sessionState.ready,
        groupsReady: sessionState.groupsReady,
        refreshGroups,
        recordCustomerSettings,
        refreshCustomerConfig,
        refreshAllStaff,
        staffPending: sessionState.staffPending,
        groupsMap: sessionState.groupsMap,
        imagesMap: sessionState.imagesMap,
        staff: sessionState.staff,
        staffMap: sessionState.staffMap,
        ccatToGcat: sessionState.ccatToGcat,
        refreshCcatToGcat,
        invites: sessionState.invites,
        invitesPending: sessionState.invitesPending,
        invitesError: sessionState.invitesError,
        invitesReady: sessionState.invitesReady,
        refreshInvites,
        licenses: sessionState.licenses,
        multiCaseSources,
        licensesPending: sessionState.licensesPending,
        licensesReady: sessionState.licensesReady,
        licensesError: sessionState.licensesError,
        refreshLicenses,
        sites: sessionState.sites,
        sitesPending: sessionState.sitesPending,
        sitesReady: sessionState.sitesReady,
        sitesError: sessionState.sitesError,
        refreshSites,
        getPrivileges,
        refreshPrivileges,
        privilegesMap: sessionState.privilegesMap,
        pendingDeletionsByRlid: sessionState.pendingDeletionsByRlid,
        pendingDeletionsReady: sessionState.pendingDeletionsReady,
        refreshPendingDeletions,
      },
    }),
    [
      refreshGroups, sessionState.baseConfig, refreshCustomerConfig,
      sessionState.groupsReady, sessionState.ready, recordCustomerSettings,
      sessionState.zones, sessionState.groupsMap, sessionState.imagesMap,
      sessionState.ccatToGcat, refreshCcatToGcat, refreshAllStaff,
      sessionState.staff, sessionState.staffMap, sessionState.staffPending,
      refreshInvites, sessionState.invites, sessionState.invitesPending,
      sessionState.invitesError, sessionState.invitesReady,
      sessionState.licenses, multiCaseSources, sessionState.licensesPending,
      sessionState.licensesReady, sessionState.licensesError,
      refreshLicenses,
      sessionState.sites, sessionState.sitesPending,
      sessionState.sitesReady, sessionState.sitesError,
      refreshSites,
      getPrivileges, refreshPrivileges, sessionState.privilegesMap,
      sessionState.pendingDeletionsByRlid, sessionState.pendingDeletionsReady,
      refreshPendingDeletions,
    ],
  );

  return (
    <TownConfigContext.Provider value={contextValue}>
      {children}
    </TownConfigContext.Provider>
  );
}

TownConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TownConfigProvider;
